import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { API_BASE_URL } from './api-base';

export interface ControlTask {
  id: number;
  task_code?: string;
  tenant_id: number;
  title: string;
  description: string | null;
  visibility: string;
  status: string;
  priority: string;
  start_date: string | null;
  due_date: string | null;
  project_id?: number | null;
  subgroup_id?: number | null;
  subgroup_name?: string | null;
  bucket_id?: number | null;
  bucket_name?: string | null;
  created_by: number;
  created_at: string;
  updated_at: string | null;
  execution_started_at?: string | null;
  execution_total_seconds?: number | null;
  execution_current_seconds?: number | null;
  execution_elapsed_seconds?: number | null;
  creator_name?: string;
  groups: any[];
  tags: any[];
  members: any[];
  subtasks: any[];
  attachments: any[];
  comments?: any[];
  history?: any[];
}

@Injectable({ providedIn: 'root' })
export class ControltaskService {
  private readonly baseUrl = `${API_BASE_URL}/controltask`;

  constructor(private http: HttpClient) {}

  getTasks(tenantId: number, extra: Record<string, any> = {}): Observable<ControlTask[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    Object.entries(extra).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    });
    return this.http.get<ControlTask[]>(`${this.baseUrl}/tasks`, { params });
  }

  getDashboard(tenantId: number, extra: Record<string, any> = {}) {
    let params = new HttpParams().set('tenant_id', tenantId);
    Object.entries(extra).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    });
    return this.http.get(`${this.baseUrl}/dashboard`, { params });
  }

  getGroups(tenantId: number, context: { user_id?: number | null; department_id?: number | null } = {}) {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (context.user_id) {
      params = params.set('user_id', context.user_id);
    }
    if (context.department_id) {
      params = params.set('department_id', context.department_id);
    }
    return this.http.get<any[]>(`${this.baseUrl}/groups`, { params });
  }

  createGroup(payload: any) {
    return this.http.post(`${this.baseUrl}/groups`, payload);
  }

  getTags(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<any[]>(`${this.baseUrl}/tags`, { params });
  }

  createTag(payload: any) {
    return this.http.post(`${this.baseUrl}/tags`, payload);
  }

  getSubgroups(
    tenantId: number,
    groupId?: number,
    context: { user_id?: number | null; department_id?: number | null } = {}
  ) {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (groupId) {
      params = params.set('group_id', groupId);
    }
    if (context.user_id) {
      params = params.set('user_id', context.user_id);
    }
    if (context.department_id) {
      params = params.set('department_id', context.department_id);
    }
    return this.http.get<any[]>(`${this.baseUrl}/subgroups`, { params });
  }

  createSubgroup(payload: any) {
    return this.http.post(`${this.baseUrl}/subgroups`, payload);
  }

  getBuckets(tenantId: number, subgroupId?: number) {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (subgroupId) {
      params = params.set('subgroup_id', subgroupId);
    }
    return this.http.get<any[]>(`${this.baseUrl}/buckets`, { params });
  }

  createBucket(payload: any) {
    return this.http.post(`${this.baseUrl}/buckets`, payload);
  }

  getUsers(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<any[]>(`${this.baseUrl}/users`, { params });
  }

  createTask(payload: any) {
    return this.http.post<{ id: number }>(`${this.baseUrl}/tasks`, payload);
  }

  updateTask(taskId: number, payload: any) {
    return this.http.put(`${this.baseUrl}/tasks/${taskId}`, payload);
  }

  deleteTask(taskId: number, tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/tasks/${taskId}`, { params }).pipe(
      catchError((error) => {
        if (error?.status === 404 || error?.status === 405) {
          return this.http.post(`${this.baseUrl}/tasks/${taskId}/delete`, { tenant_id: tenantId });
        }
        return throwError(() => error);
      })
    );
  }

  createSubtask(taskId: number, payload: any) {
    return this.http.post(`${this.baseUrl}/tasks/${taskId}/subtasks`, payload);
  }

  updateSubtask(subtaskId: number, payload: any) {
    return this.http.patch(`${this.baseUrl}/subtasks/${subtaskId}`, payload);
  }

  deleteSubtask(subtaskId: number) {
    return this.http.delete(`${this.baseUrl}/subtasks/${subtaskId}`).pipe(
      catchError((error) => {
        if (error?.status === 404) {
          return this.http.post(`${this.baseUrl}/subtasks/${subtaskId}/delete`, {});
        }
        return throwError(() => error);
      })
    );
  }

  addComment(taskId: number, payload: any) {
    if (typeof FormData !== 'undefined' && payload instanceof FormData) {
      return this.http.post(`${this.baseUrl}/tasks/${taskId}/comments`, payload);
    }
    return this.http.post(`${this.baseUrl}/tasks/${taskId}/comments`, payload);
  }

  updateComment(commentId: number, payload: any) {
    return this.http.patch(`${this.baseUrl}/comments/${commentId}`, payload);
  }

  deleteComment(tenantId: number, commentId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/comments/${commentId}`, { params });
  }

  uploadAttachment(taskId: number, file: File, uploadedBy: number) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploaded_by', String(uploadedBy));
    return this.http.post(`${this.baseUrl}/tasks/${taskId}/attachments`, formData);
  }

  removeAttachment(attachmentId: number) {
    return this.http.delete(`${this.baseUrl}/attachments/${attachmentId}`);
  }

  removeCommentAttachment(tenantId: number, attachmentId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/comment-attachments/${attachmentId}`, { params });
  }
}
